import React, { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import {
  doc,
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  serverTimestamp,
  updateDoc,
  getDoc,
  setDoc,
  increment
} from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import CryptoJS from "crypto-js";

const ENCRYPTION_KEY = process.env.REACT_APP_CHAT_ENCRYPTION_KEY || "nabi-secure-chat-key-default-2026";
const FALLBACK_KEY = "nabi-secure-chat-key-default-2026";

const encryptMessage = (text) => {
  if (!text) return "";
  try {
    return CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
  } catch (err) {
    console.error("Encryption failed", err);
    return text;
  }
};

const decryptMessage = (cipherText) => {
  if (!cipherText || cipherText === "No messages yet") return cipherText;
  try {
    // 1. Try primary key
    let bytes = CryptoJS.AES.decrypt(cipherText, ENCRYPTION_KEY);
    let originalText = bytes.toString(CryptoJS.enc.Utf8);
    if (originalText) return originalText;

    // 2. Try fallback key if it's different (handles transition period)
    if (ENCRYPTION_KEY !== FALLBACK_KEY) {
      bytes = CryptoJS.AES.decrypt(cipherText, FALLBACK_KEY);
      originalText = bytes.toString(CryptoJS.enc.Utf8);
      if (originalText) return originalText;
    }

    return cipherText; 
  } catch (err) {
    return cipherText; 
  }
};

/**
 * LiveChat Component
 * @param {string} sessionId - The Firestore ID of the chat session
 * @param {object} currentUser - The logged-in user object {uid, displayName, role}
 * @param {function} onEnd - Callback when session ends
 */
export default function LiveChat({ sessionId, currentUser, patientName, onEnd, initialData, userSessions, onSessionSwitch }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [sessionData, setSessionData] = useState(null);
  const [showReplyToast, setShowReplyToast] = useState(false);
  const scrollRef = useRef(null);

  // 1. Listen for Session Metadata
  useEffect(() => {
    if (!sessionId) return;
    if (sessionId) console.log("Active Session ID:", sessionId);

    // Initialize session document if it doesn't exist
    const initSession = async () => {
      const sessionRef = doc(db, "chat_sessions", sessionId);
      try {
        const snap = await getDoc(sessionRef);
        if (!snap.exists() && initialData) {
          await setDoc(sessionRef, {
            ...initialData,
            status: "active",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      } catch (err) {
        console.error("Error init session:", err);
      }
    };
    initSession();

    const sessionRef = doc(db, "chat_sessions", sessionId);
    const unsubscribe = onSnapshot(sessionRef, (docSnap) => {
      if (docSnap.exists()) {
        setSessionData(docSnap.data());
      }
    });
    return () => unsubscribe();
  }, [sessionId, initialData]);

  // Mark as read when opening or receiving new messages while open
  useEffect(() => {
    if (!sessionId || !sessionData) return;
    
    // Check if there are unread messages for the current user and reset them
    if (currentUser.role === 'therapist' && (sessionData.unreadCountByTherapist > 0 || sessionData.unreadByTherapist)) {
      updateDoc(doc(db, "chat_sessions", sessionId), {
        unreadCountByTherapist: 0,
        unreadByTherapist: false
      }).catch(err => console.error("Error clearing unread:", err));
    } else if (currentUser.role !== 'therapist' && (sessionData.unreadCountByPatient > 0 || sessionData.unreadByPatient)) {
      updateDoc(doc(db, "chat_sessions", sessionId), {
        unreadCountByPatient: 0,
        unreadByPatient: false
      }).catch(err => console.error("Error clearing unread:", err));
    }
  }, [sessionId, sessionData?.unreadCountByTherapist, sessionData?.unreadCountByPatient, sessionData?.unreadByTherapist, sessionData?.unreadByPatient, currentUser.role]);

  // 2. Listen for Messages from the flat 'chat_messages' collection
  useEffect(() => {
    if (!sessionId) return;
    // We remove the Firestore orderBy to avoid needing a composite index,
    // and instead sort the messages in the client.
    const messagesRef = collection(db, "chat_messages");
    const q = query(messagesRef, where("sessionId", "==", sessionId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log(`[CHAT RECEIVE] ${snapshot.size} messages in session ${sessionId}`);
      const msgs = snapshot.docs.map(doc => {
        const data = doc.data();
        const decrypted = decryptMessage(data.text);
        console.log(`[CHAT DECRYPT] MsgId: ${doc.id}, Raw: ${data.text.substring(0, 10)}..., Decrypted: ${decrypted}`);
        return {
          id: doc.id,
          ...data,
          text: decrypted
        };
      });
      // Sort by timestamp (handling potential nulls from serverTimestamp)
      msgs.sort((a, b) => {
        const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : (a.timestamp ? new Date(a.timestamp).getTime() : Date.now());
        const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : (b.timestamp ? new Date(b.timestamp).getTime() : Date.now());
        return timeA - timeB;
      });
      setMessages(msgs);
      scrollToBottom();

      // Sync lastMessage to session doc if needed
      if (msgs.length > 0) {
        const lastMsg = msgs[msgs.length - 1];
        if (lastMsg.text && (!sessionData || sessionData.lastMessage !== lastMsg.text)) {
          console.log("Syncing lastMessage to session doc:", lastMsg.text);
          setDoc(doc(db, "chat_sessions", sessionId), {
            lastMessage: lastMsg.text,
            updatedAt: lastMsg.timestamp || serverTimestamp()
          }, { merge: true }).catch(err => console.error("Sync error:", err));
        }
      }
    }, (err) => {
      console.error("Chat Messages Error:", err);
    });
    return () => unsubscribe();
  }, [sessionId]);

  // Countdown timer removed

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || !sessionId) return;
    // Only block if status is explicitly completed
    if (sessionData?.status === "completed") return;

    const text = inputText;
    setInputText(""); // Optimistic clear

    try {
      const senderName = currentUser.role === 'therapist'
        ? (currentUser.displayName || currentUser.name || "Therapist")
        : (currentUser.displayName || currentUser.name || "Patient");

      // 1. Add Message to flat collection (Encrypted)
      const encryptedText = encryptMessage(text);
      console.log(`[CHAT SEND] Plain: ${text}, Encrypted: ${encryptedText}, Session: ${sessionId}`);
      await addDoc(collection(db, "chat_messages"), {
        sessionId: sessionId,
        senderId: currentUser.uid,
        senderName: senderName,
        text: encryptedText, // Store encrypted text in Firestore
        timestamp: serverTimestamp()
      });

      // 2. Update Session Metadata for Inbox Preview (Encrypted)
      console.log(`[CHAT SYNC] Updating session preview for ${sessionId}`);
      
      const updateData = {
        lastMessage: encryptedText, // Now storing encrypted preview in session doc too
        updatedAt: serverTimestamp(),
        therapistId: sessionData?.therapistId || initialData?.therapistId || (currentUser.role === 'therapist' ? currentUser.uid : null),
        therapistEmail: sessionData?.therapistEmail || initialData?.therapistEmail || (currentUser.role === 'therapist' ? currentUser.email : null)
      };

      if (currentUser.role === 'therapist') {
         updateData.unreadCountByPatient = increment(1);
         updateData.unreadByPatient = true;
         // Legacy fields just in case
         updateData.unread = true;
         updateData.unreadCount = increment(1);
      } else {
         updateData.unreadCountByTherapist = increment(1);
         updateData.unreadByTherapist = true;
         // Legacy fields just in case
         updateData.unread = true;
         updateData.unreadCount = increment(1);
      }

      await setDoc(doc(db, "chat_sessions", sessionId), updateData, { merge: true });

      // Show toast if patient sends message
      if (currentUser.role !== 'therapist') {
        setShowReplyToast(true);
        setTimeout(() => setShowReplyToast(false), 5000);
      }
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  const handleSessionEnd = async () => {
    if (sessionData?.status === "active") {
      try {
        await updateDoc(doc(db, "chat_sessions", sessionId), {
          status: "completed"
        });
      } catch (err) {
        console.error("Error ending session:", err);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getMessageDateLabel = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp.toMillis ? timestamp.toMillis() : timestamp);
    const now = new Date();
    
    const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowWithoutTime = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const diffTime = nowWithoutTime.getTime() - dateWithoutTime.getTime();
    const diffDays = Math.round(diffTime / (1000 * 3600 * 24));
    
    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  return (
    <div style={styles.chatContainer}>
      {/* Header with Timer */}
      <div style={styles.chatHeader}>
        <div style={styles.headerInfo}>
          <div style={{ ...styles.statusDot, backgroundColor: sessionData?.status === 'active' ? '#4CAF50' : '#FFC107' }} />
          <div>
            <h3 style={styles.headerTitle}>
              {currentUser.role === 'therapist' ? `Chat with ${patientName || sessionData?.userName || 'Patient'}` : `Chat with ${sessionData?.therapistName || 'Therapist'}`}
            </h3>
            <p style={styles.headerSub}>
              {sessionData?.status === 'pending' ? 'Waiting for therapist to join...' : 'Active Session'}
              {sessionData?.packageType && ` • ${sessionData.packageType} (${sessionData.sessionsUsed}/${sessionData.totalSessions})`}
            </p>
          </div>
        </div>

        {/* History Dropdown */}
        {userSessions && userSessions.length > 1 && (
          <div style={styles.sessionDropdownWrapper}>
            {/* <label style={styles.sessionLabel}>Active Chats:</label> */}
            <select 
              value={sessionId} 
              onChange={(e) => onSessionSwitch && onSessionSwitch(e.target.value)}
              style={styles.sessionSelect}
            >
              {userSessions.map((s) => {
                const ts = s.bookingDate || s.createdAt || s.updatedAt;
                let dateLabel = "";
                if (ts) {
                  const d = new Date(ts.toMillis ? ts.toMillis() : ts);
                  dateLabel = d.toLocaleDateString([], {month: 'short', day: 'numeric', year: 'numeric'});
                }
                return (
                  <option key={s.id} value={s.id}>
                    {s.packageType || "General Chat"} {dateLabel ? `(Started: ${dateLabel})` : ""}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div style={styles.messageArea}>
        {messages.map((msg, index) => {
          const isMe = msg.senderId === currentUser.uid;
          
          let showDateLabel = false;
          let dateLabel = "";
          
          if (msg.timestamp) {
             dateLabel = getMessageDateLabel(msg.timestamp);
             if (index === 0) {
                 showDateLabel = true;
             } else {
                 const prevMsg = messages[index - 1];
                 const prevDateLabel = getMessageDateLabel(prevMsg.timestamp);
                 if (dateLabel !== prevDateLabel) {
                     showDateLabel = true;
                 }
             }
          }

          return (
            <React.Fragment key={msg.id || index}>
              {showDateLabel && (
                <div style={styles.dateLabelContainer}>
                  <span style={styles.dateLabelText}>{dateLabel}</span>
                </div>
              )}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  ...styles.messageWrapper,
                  justifyContent: isMe ? 'flex-end' : 'flex-start'
                }}
              >
                <div style={{
                  ...styles.messageBubble,
                  backgroundColor: isMe ? '#F07167' : '#FFFFFF',
                  color: isMe ? '#FFFFFF' : '#333',
                  borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                }}>
                  <p style={styles.messageText}>{msg.text}</p>
                  <span style={{
                    fontSize: '10px',
                    opacity: 0.6,
                    alignSelf: 'flex-end',
                    marginTop: '4px'
                  }}>
                    {msg.timestamp ? new Date(msg.timestamp.toMillis ? msg.timestamp.toMillis() : msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}
                  </span>
                </div>
              </motion.div>
            </React.Fragment>
          );
        })}
        <div ref={scrollRef} />
      </div>

      {/* Input Area */}
      {(() => {
        const currentSessionFromList = userSessions?.find(s => s.id === sessionId);
        const isExpired = 
          currentSessionFromList?.isExpired || 
          sessionData?.status === "completed" || 
          sessionData?.status === "expired";

        return (
          <form style={styles.inputArea} onSubmit={handleSendMessage}>
            <input
              style={styles.textInput}
              placeholder={(!sessionData) ? "Connecting..." : (isExpired ? "Package Expired (Read-only)" : "Type your message...")}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              disabled={isExpired}
            />
            <button
              style={{
                ...styles.sendBtn,
                opacity: (!inputText.trim() || isExpired) ? 0.5 : 1
              }}
              type="submit"
              disabled={!inputText.trim() || isExpired}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </form>
        );
      })()}

      <AnimatePresence>
        {showReplyToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={styles.replyToast}
          >
            💬 Thank you for reaching out! Your therapist will respond within 12 hours.
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const styles = {
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    backgroundColor: '#FFF7F1',
    boxShadow: 'none',
    overflow: 'hidden',
    position: 'relative'
  },
  chatHeader: {
    padding: '16px 24px',
    backgroundColor: '#FFF',
    borderBottom: '1px solid #F1E0D0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 'auto'
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#4CAF50',
    boxShadow: '0 0 0 4px rgba(76,175,80,0.1)'
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#003049',
    margin: 0
  },
  headerSub: {
    fontSize: '12px',
    color: '#888',
    margin: 0
  },
  timerBox: {
    textAlign: 'right',
    display: 'flex',
    flexDirection: 'column'
  },
  timerLabel: {
    fontSize: '10px',
    textTransform: 'uppercase',
    fontWeight: '700',
    letterSpacing: '0.5px',
    opacity: 0.6
  },
  timerValue: {
    fontSize: '18px',
    fontWeight: '800',
    fontFamily: 'monospace'
  },
  sessionDropdownWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  sessionLabel: {
    fontSize: '12px',
    color: '#888',
    fontWeight: '600'
  },
  sessionSelect: {
    padding: '6px 10px',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    backgroundColor: '#F9FAFB',
    fontSize: '13px',
    color: '#333',
    outline: 'none',
    cursor: 'pointer',
    maxWidth: '200px',
    textOverflow: 'ellipsis'
  },
  messageArea: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: '#FFF7F1'
  },
  messageWrapper: {
    display: 'flex',
    width: '100%'
  },
  messageBubble: {
    maxWidth: '80%',
    padding: '12px 16px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column'
  },
  senderName: {
    fontSize: '10px',
    fontWeight: '700',
    margin: '0 0 4px',
    opacity: 0.6
  },
  messageText: {
    fontSize: '14px',
    margin: 0,
    lineHeight: '1.4'
  },
  inputArea: {
    padding: '16px',
    backgroundColor: '#FFF',
    borderTop: '1px solid #F1E0D0',
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  textInput: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
    fontSize: '14px',
    outline: 'none',
    backgroundColor: '#F9FAFB',
    transition: 'border 0.2s'
  },
  sendBtn: {
    backgroundColor: '#F07167',
    color: 'white',
    border: 'none',
    width: '42px',
    height: '42px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(240,113,103,0.3)',
    transition: 'transform 0.1s'
  },
  dateLabelContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: '16px 0 8px 0',
    width: '100%'
  },
  dateLabelText: {
    backgroundColor: '#EAEAEA',
    color: '#666',
    fontSize: '11px',
    fontWeight: '600',
    padding: '4px 12px',
    borderRadius: '12px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  replyToast: {
    position: 'absolute',
    bottom: '80px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 48, 73, 0.95)',
    color: 'white',
    padding: '12px 20px',
    borderRadius: '24px',
    fontSize: '13px',
    fontWeight: '500',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    zIndex: 10,
    maxWidth: '90%',
    textAlign: 'center',
    lineHeight: '1.4',
    pointerEvents: 'none'
  }
};
